import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { getViewerTimezone } from "../../lib/timezone";
import type { DateRange } from "../../hooks/useGlobalDateRange";
import { shiftRangeByMonths } from "../../utils/dateRange";

// =============================================================================
// DATE RANGE PICKER
// =============================================================================
// PURPOSE: Dual-month calendar picker for selecting date ranges.
//
// REQUIREMENTS:
// - Shows 2 months side-by-side (default: 3 months ago to now)
// - Click & drag to select range, or click individual days
// - Prev/next navigation buttons
// - Dropdown closes on outside click or ESC
// =============================================================================

interface DateRangePickerProps {
  dateRange: DateRange;
  setDateRange: (value: DateRange | ((prev: DateRange) => DateRange)) => void;
  navigateDateRange: (direction: "prev" | "next") => void;
  filterLabel?: string;
  /** Opt-in: open on hover with the /triangle safe-close (desktop only). Default click-to-open. */
  triangle?: boolean;
}

// Helpers
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isInRange = (date: Date, start: Date | null, end: Date | null) => {
  if (!start || !end) return false;
  const d = date.getTime();
  const s = start.getTime();
  const e = end.getTime();
  return d >= Math.min(s, e) && d <= Math.max(s, e);
};

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

// SAFE-TRIANGLE (/triangle, opt-in via the `triangle` prop): hover-open + defer
// the CLOSE while the cursor dives from the trigger down into the calendar. No-pin
// variant — the desktop calendar applies selections live (no Done button), so
// leaving just closes, never losing a pick. Panel drops DOWNWARD so the aim test
// uses INVERSE slope (dx/dy) on the top corners. Same machinery as Sidebar.tsx
// OrgFlyout (977fccca). Desktop only; touch keeps the bottom sheet (no hover).
const FLYOUT_CLOSE_DELAY = 220; // ms — grace before the panel closes on leave
const AIM_RECHECK = 200; // ms — re-check cadence while the cursor aims at the panel
const MOUSE_TRACK = 3; // recent cursor samples kept for the aim calculation
const invSlope = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  (b.x - a.x) / (b.y - a.y || 0.0001);

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateRange,
  setDateRange,
  navigateDateRange,
  filterLabel,
  triangle = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Start from 3 months ago to show past + future
  const [baseMonth, setBaseMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    return d;
  });
  const [dragStart, setDragStart] = useState<Date | null>(null);
  const [dragEnd, setDragEnd] = useState<Date | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [mobileStartTap, setMobileStartTap] = useState<Date | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8, // 8px gap below button
        right: window.innerWidth - rect.right, // Align to right edge of button
      });
    }
  }, [isOpen]);

  // Number of months to show
  const MONTHS_TO_SHOW = 6;

  // Ref for the portal dropdown (desktop)
  const portalDropdownRef = useRef<HTMLDivElement>(null);

  // Safe-triangle (opt-in via `triangle`) — hover-open + defer-close, no-pin.
  const hideTimer = useRef<number | null>(null);
  const aimTimer = useRef<number | null>(null);
  const mouseLocs = useRef<{ x: number; y: number }[]>([]);
  const clearHide = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const clearAim = () => {
    if (aimTimer.current) {
      window.clearTimeout(aimTimer.current);
      aimTimer.current = null;
    }
  };
  // Is the cursor DIVING toward the open desktop panel below? Transposed aim cone.
  const aimingAtPanel = () => {
    const panel = portalDropdownRef.current;
    if (!isOpen || !panel) return false;
    const locs = mouseLocs.current;
    const loc = locs[locs.length - 1];
    const prev = locs[locs.length - 2] || loc;
    if (!loc || loc === prev) return false;
    const r = panel.getBoundingClientRect();
    if (loc.y >= r.top || prev.y >= r.top) return false; // only while still above the panel
    const BUF = 40;
    const left = { x: r.left - BUF, y: r.top };
    const right = { x: r.right + BUF, y: r.top };
    return (
      invSlope(loc, left) < invSlope(prev, left) && invSlope(loc, right) > invSlope(prev, right)
    );
  };
  const maybeHide = () => {
    if (aimingAtPanel()) {
      const settledAt = mouseLocs.current[mouseLocs.current.length - 1];
      aimTimer.current = window.setTimeout(() => {
        const now = mouseLocs.current[mouseLocs.current.length - 1];
        if (now === settledAt) {
          clearAim();
          setIsOpen(false);
        } else {
          maybeHide(); // cursor still diving toward the panel → keep it alive
        }
      }, AIM_RECHECK);
    } else {
      setIsOpen(false);
    }
  };
  const scheduleHide = () => {
    if (!triangle) return;
    clearHide();
    clearAim();
    hideTimer.current = window.setTimeout(maybeHide, FLYOUT_CLOSE_DELAY);
  };
  const openNow = () => {
    if (!triangle) return;
    clearHide();
    clearAim();
    setIsOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check both the main container and the portal dropdown
      const clickedOutsideMain = dropdownRef.current && !dropdownRef.current.contains(target);
      const clickedOutsidePortal =
        portalDropdownRef.current && !portalDropdownRef.current.contains(target);

      // Close if clicked outside both (or portal doesn't exist). Selections are
      // already live-committed, so there is nothing to discard — just dismiss.
      if (clickedOutsideMain && (!portalDropdownRef.current || clickedOutsidePortal)) {
        setMobileStartTap(null);
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Track the cursor while open, for the safe-triangle aim test (opt-in).
  useEffect(() => {
    if (!triangle || !isOpen) return;
    const onMove = (e: MouseEvent) => {
      const locs = mouseLocs.current;
      locs.push({ x: e.clientX, y: e.clientY });
      if (locs.length > MOUSE_TRACK) locs.shift();
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [triangle, isOpen]);

  // Clear safe-triangle timers on unmount.
  useEffect(
    () => () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      if (aimTimer.current) window.clearTimeout(aimTimer.current);
    },
    []
  );

  // End drag on mouse up — commit LIVE (no Done gate). Fires exactly ONCE per
  // drag, on release — NOT on every handleDayMouseEnter intermediate, which
  // would trigger ~15 dashboard reloads in a single drag. A single click
  // (mousedown+mouseup, no move) lands here too with start===end = single-day
  // range. Picker auto-closes so the dashboard result is immediately visible
  // (same behavior as presets).
  useEffect(() => {
    const handleMouseUp = () => {
      if (isDragging && dragStart && dragEnd) {
        const start = dragStart < dragEnd ? dragStart : dragEnd;
        const end = dragStart < dragEnd ? dragEnd : dragStart;
        setDateRange({ start, end, isAllTime: false });
        setIsOpen(false);
      }
      setIsDragging(false);
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [isDragging, dragStart, dragEnd, setDateRange]);

  const applyPreset = (
    preset:
      | "today"
      | "yesterday"
      | "4d"
      | "7d"
      | "lastWeek"
      | "14d"
      | "30d"
      | "1stHalf"
      | "2ndHalf"
      | "thisMonth"
      | "lastMonth"
      | "all"
      | "thisWeek"
      | "thisYear"
      | "q1"
      | "q2"
      | "q3"
      | "q4"
  ) => {
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = now;

    switch (preset) {
      case "today":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case "yesterday":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        break;
      case "4d":
        start = new Date(now);
        start.setDate(now.getDate() - 3);
        break;
      case "7d":
        start = new Date(now);
        start.setDate(now.getDate() - 6);
        break;
      case "lastWeek": {
        // Last Sunday → last Saturday (most recent completed Sun–Sat week).
        // dow=0(Sun): end = yesterday's Sat. dow=6(Sat): end = Sat 6 days ago.
        const dow = now.getDay();
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow - 1);
        start = new Date(end);
        start.setDate(end.getDate() - 6);
        break;
      }
      case "14d":
        start = new Date(now);
        start.setDate(now.getDate() - 13);
        break;
      case "30d":
        start = new Date(now);
        start.setDate(now.getDate() - 29);
        break;
      case "1stHalf":
        // 1st to 15th of current month
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth(), 15);
        break;
      case "2ndHalf":
        // 16th to end of current month
        start = new Date(now.getFullYear(), now.getMonth(), 16);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of month
        break;
      case "thisMonth":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "lastMonth":
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "all": {
        // D1: Resolve to today − 730d (covers all current Dynamis data; GHL starts 2025-12-16)
        // D2: Set isAllTime=true so picker label, nav, and compare-period logic can branch on it
        const ALL_TIME_DAYS = 730;
        start = new Date(now);
        start.setDate(now.getDate() - (ALL_TIME_DAYS - 1));
        end = new Date(now);
        setDateRange({ start, end, isAllTime: true });
        setMobileStartTap(null);
        setIsOpen(false);
        return; // skip the shared setDateRange below
      }
      case "thisWeek": {
        // Current week's Sunday → today (mirrors thisMonth — period start, no future).
        const dow = now.getDay();
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      }
      case "thisYear":
        // Jan 1 of current year → today (mirrors thisMonth pattern)
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case "q1":
        // Jan 1 → Mar 31 of current year
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 2, 31);
        break;
      case "q2":
        // Apr 1 → Jun 30 of current year
        start = new Date(now.getFullYear(), 3, 1);
        end = new Date(now.getFullYear(), 5, 30);
        break;
      case "q3":
        // Jul 1 → Sep 30 of current year
        start = new Date(now.getFullYear(), 6, 1);
        end = new Date(now.getFullYear(), 8, 30);
        break;
      case "q4":
        // Oct 1 → Dec 31 of current year
        start = new Date(now.getFullYear(), 9, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
    }
    // Presets apply immediately — no Done gate needed
    // isAllTime: false — only the "all" case sets true (handled above with early return)
    setDateRange({ start, end, isAllTime: false });
    setMobileStartTap(null);
    setIsOpen(false);
  };

  const handleDayMouseDown = useCallback((day: Date) => {
    setIsDragging(true);
    setDragStart(day);
    setDragEnd(day);
  }, []);

  const handleDayMouseEnter = useCallback(
    (day: Date) => {
      if (isDragging) {
        setDragEnd(day);
      }
    },
    [isDragging]
  );

  // Mobile: two-tap, both taps commit LIVE (no Done gate).
  // 1st tap: commit a single-day range immediately (you see the dashboard
  //   update) but KEEP the sheet open and remember the start so a 2nd tap can
  //   extend it into a range.
  // 2nd tap: commit the full range live and close the sheet.
  const handleDayTap = useCallback(
    (day: Date) => {
      if (!mobileStartTap) {
        setMobileStartTap(day);
        setDateRange({ start: day, end: day, isAllTime: false });
      } else {
        const start = mobileStartTap < day ? mobileStartTap : day;
        const end = mobileStartTap < day ? day : mobileStartTap;
        setDateRange({ start, end, isAllTime: false });
        setMobileStartTap(null);
        setIsOpen(false);
      }
    },
    [mobileStartTap, setDateRange]
  );

  // Generate array of months to display
  const monthsToRender = useMemo(() => {
    const months: { year: number; month: number }[] = [];
    for (let i = 0; i < MONTHS_TO_SHOW; i++) {
      const d = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + i, 1);
      months.push({ year: d.getFullYear(), month: d.getMonth() });
    }
    return months;
  }, [baseMonth]);

  const scrollMonths = (direction: "prev" | "next") => {
    setBaseMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + (direction === "next" ? 2 : -2));
      return d;
    });
  };

  // cellSize drives every dimension so the same grid renders compact on the
  // desktop mouse dropdown (22px) and finger-friendly on the touch bottom
  // sheet (40px ≥ Apple HIG min target). Touch passes 40; desktop passes 22.
  const renderMonth = (year: number, month: number, cellSize = 22) => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days: React.ReactNode[] = [];
    const today = new Date();
    const monthKey = `${year}-${month}`;
    const dayNames = ["S", "M", "T", "W", "T", "F", "S"];
    const monthWidth = cellSize * 7;
    const headerHeight = Math.round(cellSize * 0.72);
    const gridCols = `repeat(7, ${cellSize}px)`;
    const isTouch = cellSize >= 32;

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`${monthKey}-empty-${i}`} style={{ height: cellSize }} />);
    }

    // Day cells
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const isToday = isSameDay(date, today);
      // Selections commit live now — no pending stage. Highlight reflects the
      // committed range directly (or the in-progress drag preview below).
      const activeRange = dateRange;
      const isSelected =
        (activeRange.start && isSameDay(date, activeRange.start)) ||
        (activeRange.end && isSameDay(date, activeRange.end));
      const inRange = isDragging
        ? isInRange(date, dragStart, dragEnd)
        : isInRange(date, activeRange.start, activeRange.end);
      const isDragEdge =
        isDragging &&
        dragStart &&
        dragEnd &&
        (isSameDay(date, dragStart) || isSameDay(date, dragEnd));
      const isMobileStart = mobileStartTap && isSameDay(date, mobileStartTap);

      days.push(
        <div
          key={`${monthKey}-${d}`}
          data-day={date.toISOString()}
          onMouseDown={() => handleDayMouseDown(date)}
          onMouseEnter={() => handleDayMouseEnter(date)}
          onTouchStart={(e) => {
            touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          }}
          onTouchEnd={(e) => {
            // Two-tap selection for mobile — only treat as tap if finger didn't scroll
            if (touchStartPos.current) {
              // CRITICAL: cancel the browser's compat mouse events
              // (mousedown→mouseup→click) that fire after every touchend.
              // Without this, the synthetic mousedown hits
              // handleDayMouseDown → the document mouseup handler →
              // setDateRange + setIsOpen(false), slamming the sheet shut on
              // the FIRST tap and making the two-tap range impossible. This
              // is the spec'd way to suppress synthetic mouse events (same
              // preventDefault pattern as the right-click onContextMenu).
              e.preventDefault();
              handleDayTap(date);
              touchStartPos.current = null;
            }
          }}
          onTouchMove={() => {
            // If finger moves, it's a scroll — cancel tap detection
            touchStartPos.current = null;
          }}
          style={{ height: cellSize }}
          className={`flex items-center justify-center ${isTouch ? "token-13" : "token-calendar"} cursor-pointer select-none rounded text-glass
                        ${isToday ? "font-bold ring-1 ring-blue-400" : ""}
                        ${isSelected || isDragEdge || isMobileStart ? "bg-blue-500 text-white" : ""}
                        ${inRange && !isSelected && !isDragEdge ? "bg-blue-500/30" : ""}
                        ${!isSelected && !inRange && !isDragEdge && !isMobileStart ? "hover:bg-black/[0.06]" : ""}
                    `}
        >
          {d}
        </div>
      );
    }

    const monthName = new Date(year, month, 1).toLocaleDateString("en-US", { month: "short" });

    return (
      <div key={monthKey} className="select-none">
        {/* Month header */}
        <div
          className={`${isTouch ? "token-13" : "token-10"} mb-1 text-center text-glass font-semibold uppercase`}
          style={{ width: monthWidth }}
        >
          {monthName} {year}
        </div>
        {/* Day headers */}
        <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 0 }}>
          {dayNames.map((name, i) => (
            <div
              key={`${monthKey}-h-${i}`}
              className={`flex items-center justify-center ${isTouch ? "token-10" : "token-8"} font-medium text-glass-muted`}
              style={{ height: headerHeight }}
            >
              {name}
            </div>
          ))}
        </div>
        {/* Calendar grid */}
        <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 0 }}>{days}</div>
      </div>
    );
  };

  // Format the display range — use viewer timezone so dates match the picker selection
  const displayRange = useMemo(() => {
    // isAllTime branch wins first (D7) — prevents label showing concrete dates after All Time resolves
    if (dateRange.isAllTime) return "All Time";
    if (!dateRange.start) return "All Time"; // defensive fallback
    const viewerTz = getViewerTimezone();
    const startStr = dateRange.start.toLocaleDateString("en-US", { timeZone: viewerTz });
    const endStr = dateRange.end
      ? dateRange.end.toLocaleDateString("en-US", { timeZone: viewerTz })
      : "Now";
    return `${startStr} - ${endStr}`;
  }, [dateRange]);

  return (
    <div
      className={`liquid-glass-light p-1.5 pl-3 sm:p-2 sm:pl-4 rounded-xl flex items-center gap-2 sm:gap-3 relative ${isOpen ? "z-50" : "z-30"}`}
      ref={dropdownRef}
    >
      <div className="flex-shrink-0 hidden sm:block">
        <span className="token-13 text-glass-tertiary uppercase">Date Range</span>
      </div>
      <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
        {/* |< / >| have title tooltips because month-shift behavior is less obvious than span-aware nav */}
        <button
          type="button"
          onClick={() => setDateRange(shiftRangeByMonths(dateRange, -1))}
          onContextMenu={(e) => {
            e.preventDefault();
            if (!dateRange.start || !dateRange.end || dateRange.isAllTime) return;
            setDateRange(shiftRangeByMonths(dateRange, 1));
          }}
          disabled={!dateRange.start || !dateRange.end || !!dateRange.isAllTime}
          className="hidden sm:inline-flex p-1.5 sm:p-2 rounded hover:bg-black/[0.06] disabled:opacity-30 disabled:cursor-not-allowed text-glass-secondary flex-shrink-0"
          aria-label="Previous month"
          title="Previous month (right-click: next month)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M18 19l-7-7 7-7M7 5v14"
            />
          </svg>
        </button>
        <button
          onClick={() => navigateDateRange("prev")}
          onContextMenu={(e) => {
            e.preventDefault();
            if (!dateRange.start || dateRange.isAllTime) return;
            navigateDateRange("next");
          }}
          disabled={!dateRange.start || !!dateRange.isAllTime}
          className="p-1.5 sm:p-2 rounded hover:bg-black/[0.06] disabled:opacity-30 disabled:cursor-not-allowed text-glass-secondary flex-shrink-0 flex items-center justify-center min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 sm:inline-block"
          aria-label="Previous date range"
          title="Previous range (right-click: next range)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <div className="relative flex-1 min-w-0">
          <button
            ref={buttonRef}
            data-testid="date-range-toggle"
            onMouseEnter={triangle ? openNow : undefined}
            onMouseLeave={triangle ? scheduleHide : undefined}
            onFocus={triangle ? openNow : undefined}
            onClick={triangle ? openNow : () => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            className="anim-tap flex items-center gap-1.5 bg-black/[0.05] hover:bg-black/[0.08] border border-black/[0.12] rounded-lg px-2 sm:px-2.5 py-1.5 token-13 text-glass transition-colors w-full justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-glass-muted flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="truncate">{displayRange}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-glass-muted flex-shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {isOpen && (
            <>
              {/* Phone + tablet: full-screen bottom sheet (touch). Split moved
                  from sm: (640) to lg: (1024) so tablets get the finger-
                  friendly two-tap sheet, NOT the mouse-only drag dropdown
                  (drag needs mouseenter, which never fires on touch). */}
              <div
                className="lg:hidden fixed inset-0 bg-black/30 z-40"
                onClick={() => {
                  setMobileStartTap(null);
                  setIsOpen(false);
                }}
              />
              <div
                data-testid="date-range-sheet"
                className="lg:hidden fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
                style={{ paddingBottom: "env(safe-area-inset-bottom, 16px)" }}
              >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-2 sticky top-0 bg-white rounded-t-2xl">
                  <div className="w-10 h-1 bg-gray-300 rounded-full" />
                </div>

                {/* Preset chips */}
                <div className="px-4 pb-3 flex flex-wrap gap-2">
                  {(
                    [
                      "today",
                      "yesterday",
                      "4d",
                      "7d",
                      "lastWeek",
                      "14d",
                      "30d",
                      "1stHalf",
                      "2ndHalf",
                      "thisMonth",
                      "lastMonth",
                      "all",
                      "thisWeek",
                      "thisYear",
                      "q1",
                      "q2",
                      "q3",
                      "q4",
                    ] as const
                  ).map((p) => (
                    <button
                      key={p}
                      onClick={() => applyPreset(p)}
                      className="anim-tap px-4 py-2.5 min-h-[44px] token-12 text-glass-secondary bg-black/[0.05] active:bg-blue-500/15 active:text-blue-400 rounded-full transition-colors"
                    >
                      {p === "today"
                        ? "Today"
                        : p === "yesterday"
                          ? "Yesterday"
                          : p === "4d"
                            ? "4 Days"
                            : p === "7d"
                              ? "7 Days"
                              : p === "lastWeek"
                                ? "Last Week"
                                : p === "14d"
                                  ? "2 Weeks"
                                  : p === "30d"
                                    ? "30 Days"
                                    : p === "1stHalf"
                                      ? "1st Half"
                                      : p === "2ndHalf"
                                        ? "2nd Half"
                                        : p === "thisMonth"
                                          ? "This Month"
                                          : p === "lastMonth"
                                            ? "Last Month"
                                            : p === "all"
                                              ? "All Time"
                                              : p === "thisWeek"
                                                ? "This Week"
                                                : p === "thisYear"
                                                  ? "This Year"
                                                  : p === "q1"
                                                    ? "Q1"
                                                    : p === "q2"
                                                      ? "Q2"
                                                      : p === "q3"
                                                        ? "Q3"
                                                        : "Q4"}
                    </button>
                  ))}
                </div>

                {/* Month navigation */}
                <div className="flex items-center justify-between px-4 mb-2">
                  <button
                    onClick={() => scrollMonths("prev")}
                    className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center active:bg-black/[0.06] rounded text-glass-secondary"
                    aria-label="Scroll to previous months"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <span className="token-10 text-center text-glass-muted">
                    Tap a date — applies instantly. Tap again for a range.
                  </span>
                  <button
                    onClick={() => scrollMonths("next")}
                    className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center active:bg-black/[0.06] rounded text-glass-secondary"
                    aria-label="Scroll to next months"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Touch month grid: 1 col on phones, 2 on tablets. cellSize
                    40 (≥ Apple HIG 44px target incl. tap slop) — each block is
                    40×7 = 280px wide, fits a 375 phone with px-4 padding. */}
                <div className="px-4 pb-2 grid grid-cols-1 sm:grid-cols-2 gap-4 justify-center justify-items-center">
                  {monthsToRender.map(({ year, month }) => renderMonth(year, month, 40))}
                </div>

                {/* Footer — live readout only (no Done; selection commits on
                    tap). Close via backdrop tap or the drag handle. */}
                <div className="sticky bottom-0 bg-white border-t border-black/[0.08] px-4 py-3 flex items-center justify-between gap-3">
                  <div className="token-12 text-glass-muted">
                    {mobileStartTap
                      ? `${mobileStartTap.toLocaleDateString("en-US", { timeZone: getViewerTimezone() })} — tap end date for a range`
                      : dateRange.isAllTime
                        ? "All Time"
                        : dateRange.start && dateRange.end
                          ? `${dateRange.start.toLocaleDateString("en-US", { timeZone: getViewerTimezone() })} - ${dateRange.end.toLocaleDateString("en-US", { timeZone: getViewerTimezone() })}`
                          : "Tap a date"}
                  </div>
                  <button
                    onClick={() => {
                      setMobileStartTap(null);
                      setIsOpen(false);
                    }}
                    className="anim-tap token-12 py-2.5 px-5 min-h-[44px] rounded-full bg-black/[0.05] active:bg-black/[0.1] text-glass-secondary flex-shrink-0"
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Desktop: portal dropdown with fixed positioning */}
              {dropdownPosition &&
                createPortal(
                  <div
                    ref={portalDropdownRef}
                    data-testid="date-range-portal"
                    onMouseEnter={
                      triangle
                        ? () => {
                            clearHide();
                            clearAim();
                          }
                        : undefined
                    }
                    onMouseLeave={triangle ? scheduleHide : undefined}
                    className="hidden lg:flex rounded-xl shadow-2xl overflow-hidden z-[9999] liquid-glass"
                    style={{
                      position: "fixed", // Inline style to guarantee it wins over liquid-glass's position: relative
                      top: dropdownPosition.top,
                      right: dropdownPosition.right,
                    }}
                  >
                    {/* Presets column */}
                    <div className="w-[115px] border-r border-black/[0.08] p-1.5 pt-3 flex flex-col gap-0.5 bg-black/[0.03]">
                      {(
                        [
                          "today",
                          "yesterday",
                          "4d",
                          "7d",
                          "lastWeek",
                          "14d",
                          "30d",
                          "1stHalf",
                          "2ndHalf",
                          "thisMonth",
                          "lastMonth",
                          "all",
                          "thisWeek",
                          "thisYear",
                          "q1",
                          "q2",
                          "q3",
                          "q4",
                        ] as const
                      ).map((p) => (
                        <button
                          key={p}
                          onClick={() => applyPreset(p)}
                          className="text-left px-2 py-1 token-11 rounded whitespace-nowrap transition-colors text-glass-muted hover:bg-black/[0.06] hover:text-blue-300"
                        >
                          {p === "today"
                            ? "Today"
                            : p === "yesterday"
                              ? "Yesterday"
                              : p === "4d"
                                ? "Last 4 Days"
                                : p === "7d"
                                  ? "Last 7 Days"
                                  : p === "lastWeek"
                                    ? "Last Week"
                                    : p === "14d"
                                      ? "Last 2 Weeks"
                                      : p === "30d"
                                        ? "Last 30 Days"
                                        : p === "1stHalf"
                                          ? "1st Half"
                                          : p === "2ndHalf"
                                            ? "2nd Half"
                                            : p === "thisMonth"
                                              ? "This Month"
                                              : p === "lastMonth"
                                                ? "Last Month"
                                                : p === "all"
                                                  ? "All Time"
                                                  : p === "thisWeek"
                                                    ? "This Week"
                                                    : p === "thisYear"
                                                      ? "This Year"
                                                      : p === "q1"
                                                        ? "Q1"
                                                        : p === "q2"
                                                          ? "Q2"
                                                          : p === "q3"
                                                            ? "Q3"
                                                            : "Q4"}
                        </button>
                      ))}
                    </div>

                    {/* Calendar area */}
                    <div className="p-2">
                      {/* Navigation */}
                      <div className="flex items-center justify-between mb-2">
                        <button
                          onClick={() => scrollMonths("prev")}
                          className="p-1 rounded hover:bg-black/[0.06] text-glass-muted"
                          aria-label="Scroll to previous months"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>
                        <span className="token-10 font-medium text-glass-muted">
                          Drag or click — applies instantly
                        </span>
                        <button
                          onClick={() => scrollMonths("next")}
                          className="p-1 rounded hover:bg-black/[0.06] text-glass-muted"
                          aria-label="Scroll to next months"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Multi-month grid (3 columns x 2 rows) */}
                      <div
                        ref={scrollRef}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 154px)",
                          gap: 16,
                        }}
                      >
                        {monthsToRender.map(({ year, month }) => renderMonth(year, month))}
                      </div>

                      {/* Footer — live readout only. Selection commits on drag
                          release / click; no Done gate. */}
                      <div className="mt-2 pt-2 border-t border-black/[0.08] flex items-center justify-between">
                        <div className="token-11 text-glass-muted">
                          {isDragging && dragStart && dragEnd
                            ? `${(dragStart < dragEnd ? dragStart : dragEnd).toLocaleDateString("en-US", { timeZone: getViewerTimezone() })} - ${(dragStart < dragEnd ? dragEnd : dragStart).toLocaleDateString("en-US", { timeZone: getViewerTimezone() })}`
                            : dateRange.isAllTime
                              ? "All Time"
                              : dateRange.start && dateRange.end
                                ? `${dateRange.start.toLocaleDateString("en-US", { timeZone: getViewerTimezone() })} - ${dateRange.end.toLocaleDateString("en-US", { timeZone: getViewerTimezone() })}`
                                : "Drag or click to select"}
                        </div>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
            </>
          )}
        </div>

        <button
          onClick={() => navigateDateRange("next")}
          onContextMenu={(e) => {
            e.preventDefault();
            if (!dateRange.end || dateRange.isAllTime) return;
            navigateDateRange("prev");
          }}
          disabled={!dateRange.end || !!dateRange.isAllTime}
          className="p-1.5 sm:p-2 rounded hover:bg-black/[0.06] disabled:opacity-30 disabled:cursor-not-allowed text-glass-secondary flex-shrink-0 flex items-center justify-center min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 sm:inline-block"
          aria-label="Next date range"
          title="Next range (right-click: previous range)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => setDateRange(shiftRangeByMonths(dateRange, 1))}
          onContextMenu={(e) => {
            e.preventDefault();
            if (!dateRange.start || !dateRange.end || dateRange.isAllTime) return;
            setDateRange(shiftRangeByMonths(dateRange, -1));
          }}
          disabled={!dateRange.start || !dateRange.end || !!dateRange.isAllTime}
          className="hidden sm:inline-flex p-1.5 sm:p-2 rounded hover:bg-black/[0.06] disabled:opacity-30 disabled:cursor-not-allowed text-glass-secondary flex-shrink-0"
          aria-label="Next month"
          title="Next month (right-click: previous month)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M6 5l7 7-7 7M17 5v14"
            />
          </svg>
        </button>
      </div>
      {filterLabel && (
        <>
          <div className="h-6 w-px bg-black/[0.08] hidden sm:block"></div>
          <div className="token-10l text-glass-muted italic hidden sm:block">
            Filtering by <span className="token-10 text-glass-secondary">{filterLabel}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
